import styled from 'styled-components';
import { mediaLgMax, mediaLgMin, mediaMdMin, mediaSmMax } from 'helpers/breakpoints';
import { experiencesData } from 'components/homepage/utils';
import NextImageWithShimmer from 'components/image/NextImageWithShimmer';
import { useScrollSnap } from 'hooks/useScrollSnap';

type SlideItem = typeof experiencesData[number][number];

/**
 * Each slide item has a particular width. These widths are important in determining the right width of image to
 * import. These widths are static across screen sizes where this component shows, which is 1140 and greater. The
 * widths are also consistent between slide 1 and slide 2.
 */
const slideItemSizes = ['221px', '528px', '233px', '221px', '352px'];

const renderGridItems = (items: SlideItem[], slideNum: number) =>
  items.map((item, i) => (
    <a
      href={item.link}
      key={item.title}
      className={[i < 2 ? 'carouselTop' : 'carouselBottom', 'place'].join(' ')}
      style={{ gridArea: item.grid }}
      data-testid={`homepage-experiences-slide${slideNum}-experience-box`}
    >
      <NextImageWithShimmer
        src={item.s3Path}
        alt={item.title}
        fetchPriority={slideNum === 1 ? 'high' : 'low'}
        fill
        sizes={slideItemSizes[i]}
        style={{ objectPosition: item.objectPosition }}
      />
      <h3>{item.title}</h3>
      <div className="gradient" />
    </a>
  ));

const Experiences = () => {
  const slide1 = experiencesData[0];
  const slide2 = experiencesData[1];
  const mobileList: SlideItem[] = slide1.concat(slide2);

  const { scrollRef: desktopScrollRef, canScrollPrev, canScrollNext, scrollToPrev, scrollToNext } = useScrollSnap();

  return (
    <FindNextWrapper data-testid="homepage-experiences-wrapper">
      <img
        className="waves"
        src="/images/homepage/experiences/yellow-waves-032825.svg"
        alt="wave decoration"
        loading="lazy"
        width={216}
        height={182}
      />
      <div className="cap container">
        <h2>Top boating categories</h2>

        {/* Desktop (>=1140px): 2 grid pages with scroll-snap */}
        <div className="desktop-view">
          <DesktopScroll ref={desktopScrollRef}>
            <div className="grid" data-slide-index={0}>
              {renderGridItems(slide1, 1)}
            </div>
            <div className="grid" data-slide-index={1}>
              {renderGridItems(slide2, 2)}
            </div>
          </DesktopScroll>
          <div className="desktop-nav">
            <button
              type="button"
              onClick={scrollToPrev}
              className="arrow-btn"
              disabled={!canScrollPrev}
              aria-label="Previous categories"
              data-testid="homepage-experiences-left-arrow"
            >
              <img src="/images/homepage/carousel-left.svg" alt="" width={10} height={17} />
            </button>
            <button
              type="button"
              onClick={scrollToNext}
              className="arrow-btn next"
              disabled={!canScrollNext}
              aria-label="Next categories"
              data-testid="homepage-experiences-right-arrow"
            >
              <img src="/images/homepage/carousel-left.svg" alt="" width={10} height={17} />
            </button>
          </div>
        </div>

        {/* Mobile (<1140px): flat horizontal scroll of all items */}
        <MobileScroll className="mobile-view">
          {mobileList.map((item, i) => (
            <div className="mobile-card" key={`${item.title}-${i}`}>
              <a href={item.link}>
                <div className="carouselItem">
                  <NextImageWithShimmer
                    src={item.s3Path}
                    alt={item.title}
                    fill
                    loading={i < 4 ? 'eager' : 'lazy'}
                    sizes="250px"
                    style={{
                      objectPosition: item.mobileObjectPosition,
                      objectFit: 'cover',
                    }}
                  />
                  <h3>{item.title}</h3>
                  <div className="gradient" />
                </div>
              </a>
            </div>
          ))}
        </MobileScroll>
      </div>
    </FindNextWrapper>
  );
};

export default Experiences;

const DesktopScroll = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .grid {
    flex-shrink: 0;
    width: 100%;
    scroll-snap-align: start;
    display: grid;
    z-index: 32;
    grid-template-columns: 20px 233px 221px 166px 166px 156px;
    grid-template-rows: 280px 200px;
    gap: 20px;
    grid-template-areas:
      '. . pontoon yachts yachts yachts'
      '. cruising sailing fishing fishing .';
  }

  .place {
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
  }

  .carouselTop {
    height: 286px;
  }

  .carouselBottom {
    height: 200px;
  }

  .carouselTop,
  .carouselBottom {
    img {
      border-radius: 10px;
      object-fit: cover;
      transition: all 500ms ease-in-out;
    }

    &:hover {
      img {
        transform: scale(1.1);
      }

      .gradient {
        opacity: 1;
      }
    }
  }

  h3 {
    font-weight: 600;
    font-size: 24px;
    line-height: 34px;
    transition: opacity 500ms ease-in-out;
    color: white;
    z-index: 2;
    position: absolute;
    left: 20px;
    bottom: 10px;
  }
`;

const MobileScroll = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 15px;

  &::-webkit-scrollbar {
    display: none;
  }

  .mobile-card {
    flex-shrink: 0;
    scroll-snap-align: start;

    /* <640px: ~1.4 slides visible */
    width: calc((100% - 15px) / 1.4);

    /* 640-959px: ~2.3 slides */
    @media ${mediaMdMin} {
      width: calc((100% - 30px) / 2.3);
    }

    /* 960-1139px: ~3.4 slides */
    @media ${mediaLgMin} {
      width: calc((100% - 45px) / 3.4);
    }
  }

  .carouselItem {
    height: 321px;
    position: relative;
    width: 100%;
    border-radius: 10px;
    overflow: hidden;

    h3 {
      font-weight: bold;
      transition: opacity 500ms ease-in-out;
      color: white;
      z-index: 2;
      position: absolute;
      font-size: 24px;
      line-height: 30px;
      margin-bottom: 20px;
      left: 20px;
      bottom: 0;
    }
  }
`;

const FindNextWrapper = styled.div`
  background-color: #fff6e7;
  margin-top: 100px;
  margin-bottom: 169px;
  position: relative;

  @media ${mediaSmMax} {
    margin-top: 100px;
    margin-bottom: 116px;
  }
  @media ${mediaMdMin} {
    margin-top: 150px;
    margin-bottom: 150px;
  }
  @media ${mediaLgMin} {
    margin-top: 100px;
    margin-bottom: 169px;
  }

  .waves {
    max-height: 242px;
    position: absolute;
    top: calc(50% - 124px);
    height: 100%;
    transform: translateX(-65%);

    @media ${mediaSmMax} {
      transform: translateX(-84%);
    }
  }

  .container {
    transform: translateY(50px);

    .gradient {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      transition: all 0.3s ease;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 55.76%, rgba(0, 0, 0, 0.3) 100%);
    }

    h2 {
      top: 70px;
      max-width: 290px;
      position: absolute;
      color: #17233c;
      font-weight: bold;
      font-size: 32px;
      line-height: 38px;
      z-index: 33;

      @media ${mediaSmMax} {
        font-size: 24px;
        line-height: 29px;
        margin-bottom: 16px;
        padding-left: 0 !important;
      }

      @media ${mediaLgMax} {
        position: unset;
        padding-bottom: 20px;
        top: unset;
        padding-left: 40px;
      }
    }
  }

  /* Desktop: show grid view, hide mobile */
  .desktop-view {
    position: relative;
    z-index: 34;

    @media ${mediaLgMax} {
      display: none;
    }
  }

  /* Mobile: show flat scroll, hide on desktop */
  .mobile-view {
    display: none;

    @media ${mediaLgMax} {
      display: flex;
    }
  }

  .desktop-nav {
    position: absolute;
    right: 10px;
    bottom: 144px;
    z-index: 33;
    display: flex;
    gap: 20px;

    .arrow-btn {
      cursor: pointer;
      background: white;
      padding: 0;
      border-radius: 50%;
      height: 47px;
      width: 47px;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s;

      &.next {
        transform: rotateZ(180deg);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      img {
        height: 12px;
      }
    }
  }
`;
